// client/src/components/communication/CreateSessionModal.jsx
import React, { useState, useEffect } from 'react';
import '../../../styles/components/communication/CreateSessionModal.css';

const CreateSessionModal = ({
  patient,
  onClose,
  onCreateSession
}) => {
  // Modal state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);

  // Form state
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [customDateTime, setCustomDateTime] = useState('');
  const [sessionDuration, setSessionDuration] = useState(60);
  const [sessionType, setSessionType] = useState('appointment'); // 'appointment' or 'custom'
  const [sessionNotes, setSessionNotes] = useState('');

  // Load patient's upcoming appointments
  useEffect(() => {
    loadUpcomingAppointments();
  }, [patient._id]);

  const loadUpcomingAppointments = async () => {
    try {
      setLoadingAppointments(true);
      setError(null);

      // Get doctor's appointments with this specific patient
      const response = await fetch('/api/doctor/appointments', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load appointments');
      }

      const result = await response.json();
      const allAppointments = result.data || result;

      // Filter for this patient's future appointments
      const now = new Date();
      const patientAppointments = allAppointments
        .filter(appointment => 
          appointment.patient?._id === patient._id &&
          new Date(appointment.appointmentDate) > now &&
          appointment.status === 'Scheduled'
        )
        .sort((a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate));

      setAppointments(patientAppointments);

      // Auto-select the next appointment if available
      if (patientAppointments.length > 0) {
        setSelectedAppointment(patientAppointments[0]);
      }

      console.log(`✅ Loaded ${patientAppointments.length} upcoming appointments for ${patient.firstName}`);

    } catch (error) {
      console.error('❌ Error loading appointments:', error);
      setError('Failed to load appointments. You can still create a custom session.');
    } finally {
      setLoadingAppointments(false);
    }
  };

  // Handle appointment selection
  const handleAppointmentSelect = (appointment) => {
    setSelectedAppointment(appointment);
    setSessionType('appointment');
    
    // Set duration based on appointment type or default
    const appointmentDuration = appointment.duration || 60;
    setSessionDuration(appointmentDuration);
  };

  // Handle custom session toggle
  const handleCustomSessionToggle = () => {
    if (sessionType === 'custom') {
      setSessionType('appointment');
      if (appointments.length > 0) {
        setSelectedAppointment(appointments[0]);
      }
    } else {
      setSessionType('custom');
      setSelectedAppointment(null);
      
      // Set default custom time to next hour
      const nextHour = new Date();
      nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
      setCustomDateTime(nextHour.toISOString().slice(0, 16));
    }
  };

  // Validate form
  const isFormValid = () => {
    if (sessionType === 'appointment') {
      return selectedAppointment && sessionDuration > 0;
    } else {
      return customDateTime && sessionDuration > 0;
    }
  };

  // Get session time based on type
  const getSessionTime = () => {
    if (sessionType === 'appointment' && selectedAppointment) {
      return new Date(selectedAppointment.appointmentDate);
    } else if (sessionType === 'custom' && customDateTime) {
      return new Date(customDateTime);
    }
    return null;
  };

  // Format appointment time for display
  const formatAppointmentTime = (appointment) => {
    const date = new Date(appointment.appointmentDate);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const timeString = date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });

    if (date.toDateString() === today.toDateString()) {
      return `Today at ${timeString}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow at ${timeString}`;
    } else {
      return `${date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      })} at ${timeString}`;
    }
  };

  // Handle session creation
  const handleCreateSession = async () => {
    if (!isFormValid()) {
      setError('Please fill in all required fields.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const sessionTime = getSessionTime();
      if (!sessionTime) {
        throw new Error('Invalid session time');
      }

      // Validate session time is in the future
      const now = new Date();
      if (sessionTime <= now) {
        throw new Error('Session time must be in the future');
      }

      // Prepare session data
      const sessionData = {
        appointmentId: sessionType === 'appointment' ? selectedAppointment._id : null,
        sessionTime: sessionTime.toISOString(),
        sessionDuration: parseInt(sessionDuration),
        sessionNotes: sessionNotes.trim(),
        sessionType: sessionType,
        patientId: patient._id
      };

      console.log('🎥 Creating video session:', sessionData);

      // Call parent handler
      await onCreateSession(sessionData);

      console.log('✅ Video session created successfully');

      // Close modal
      onClose();

    } catch (error) {
      console.error('❌ Error creating session:', error);
      setError(error.message || 'Failed to create video session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Get minimum datetime for custom sessions (current time + 5 minutes)
  const getMinDateTime = () => {
    const minTime = new Date();
    minTime.setMinutes(minTime.getMinutes() + 5);
    return minTime.toISOString().slice(0, 16);
  };

  return (
    <div className="create-session-modal-overlay">
      <div className="create-session-modal">
        {/* Modal Header */}
        <div className="modal-header">
          <div className="header-content">
            <h2>Create Video Session</h2>
            <p>Schedule a secure video therapy session with {patient.firstName} {patient.lastName}</p>
          </div>
          <button 
            className="close-button"
            onClick={onClose}
            disabled={isLoading}
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Patient Info */}
          <div className="patient-info">
            <div className="patient-avatar">
              {patient.profilePicture ? (
                <img src={patient.profilePicture} alt={`${patient.firstName} ${patient.lastName}`} />
              ) : (
                <div className="avatar-initials">
                  {patient.firstName?.charAt(0)}{patient.lastName?.charAt(0)}
                </div>
              )}
            </div>
            <div className="patient-details">
              <h3>{patient.firstName} {patient.lastName}</h3>
              <p>{patient.email}</p>
            </div>
          </div>

          {/* Session Type Selection */}
          <div className="session-type-section">
            <h4>Session Type</h4>
            
            {/* Appointment-based Session */}
            <div className="session-type-option">
              <label className="session-type-label">
                <input
                  type="radio"
                  name="sessionType"
                  value="appointment"
                  checked={sessionType === 'appointment'}
                  onChange={() => setSessionType('appointment')}
                  disabled={appointments.length === 0 && !loadingAppointments}
                />
                <span className="radio-indicator"></span>
                <div className="option-content">
                  <span className="option-title">Based on Existing Appointment</span>
                  <span className="option-description">
                    Create a video session for a scheduled appointment
                  </span>
                </div>
              </label>

              {/* Appointments List */}
              {sessionType === 'appointment' && (
                <div className="appointments-section">
                  {loadingAppointments ? (
                    <div className="loading-appointments">
                      <div className="loading-spinner"></div>
                      <span>Loading appointments...</span>
                    </div>
                  ) : appointments.length === 0 ? (
                    <div className="no-appointments">
                      <span className="no-appointments-icon">📅</span>
                      <p>No upcoming appointments found with this patient.</p>
                      <p>You can create a custom session instead.</p>
                    </div>
                  ) : (
                    <div className="appointments-list">
                      {appointments.map((appointment) => (
                        <div
                          key={appointment._id}
                          className={`appointment-item ${
                            selectedAppointment?._id === appointment._id ? 'selected' : ''
                          }`}
                          onClick={() => handleAppointmentSelect(appointment)}
                        >
                          <div className="appointment-time">
                            <span className="time-icon">🕒</span>
                            <span>{formatAppointmentTime(appointment)}</span>
                          </div>
                          <div className="appointment-details">
                            <span className="appointment-type">
                              {appointment.type || 'Therapy Session'}
                            </span>
                            {appointment.duration && (
                              <span className="appointment-duration">
                                {appointment.duration} minutes
                              </span>
                            )}
                          </div>
                          {selectedAppointment?._id === appointment._id && (
                            <div className="selected-indicator">✓</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Custom Session */}
            <div className="session-type-option">
              <label className="session-type-label">
                <input
                  type="radio"
                  name="sessionType"
                  value="custom"
                  checked={sessionType === 'custom'}
                  onChange={() => setSessionType('custom')}
                />
                <span className="radio-indicator"></span>
                <div className="option-content">
                  <span className="option-title">Custom Session</span>
                  <span className="option-description">
                    Schedule a video session at a specific time
                  </span>
                </div>
              </label>

              {/* Custom DateTime Input */}
              {sessionType === 'custom' && (
                <div className="custom-session-inputs">
                  <div className="input-group">
                    <label htmlFor="customDateTime">Session Date & Time</label>
                    <input
                      id="customDateTime"
                      type="datetime-local"
                      value={customDateTime}
                      onChange={(e) => setCustomDateTime(e.target.value)}
                      min={getMinDateTime()}
                      required
                    />
                    <small>Sessions can be scheduled at least 5 minutes in advance</small>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Session Duration */}
          <div className="duration-section">
            <h4>Session Duration</h4>
            <div className="duration-options">
              {[30, 45, 60, 90].map((duration) => (
                <button
                  key={duration}
                  className={`duration-btn ${sessionDuration === duration ? 'selected' : ''}`}
                  onClick={() => setSessionDuration(duration)}
                  type="button"
                >
                  {duration} min
                </button>
              ))}
            </div>
            <div className="custom-duration">
              <label htmlFor="customDuration">Or enter custom duration:</label>
              <div className="duration-input-group">
                <input
                  id="customDuration"
                  type="number"
                  min="15"
                  max="180"
                  value={sessionDuration}
                  onChange={(e) => setSessionDuration(parseInt(e.target.value) || 60)}
                />
                <span>minutes</span>
              </div>
            </div>
          </div>

          {/* Session Notes */}
          <div className="notes-section">
            <h4>Session Notes (Optional)</h4>
            <textarea
              value={sessionNotes}
              onChange={(e) => setSessionNotes(e.target.value)}
              placeholder="Add any notes about this session..."
              rows="3"
              maxLength="500"
            />
            <small>{sessionNotes.length}/500 characters</small>
          </div>

          {/* Session Summary */}
          {isFormValid() && (
            <div className="session-summary">
              <h4>Session Summary</h4>
              <div className="summary-content">
                <div className="summary-item">
                  <span className="summary-icon">👤</span>
                  <span>Patient: {patient.firstName} {patient.lastName}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-icon">🕒</span>
                  <span>
                    Time: {getSessionTime()?.toLocaleString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-icon">⏱️</span>
                  <span>Duration: {sessionDuration} minutes</span>
                </div>
                <div className="summary-item">
                  <span className="summary-icon">📋</span>
                  <span>
                    Type: {sessionType === 'appointment' ? 'Appointment-based' : 'Custom'} Session
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button
            className="btn secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={handleCreateSession}
            disabled={!isFormValid() || isLoading}
          >
            {isLoading ? (
              <>
                <div className="loading-spinner small"></div>
                Creating Session...
              </>
            ) : (
              <>
                📹 Create Video Session
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSessionModal;